import { useCallback, useEffect, useRef, useState } from "react";
import { Sheet, DialogCancel } from "@cntrl/ui";
import {
  nfcAvailable,
  nfcStartReadSession,
  nfcEndReadSession,
  onNfcTagFound,
  onNfcSessionEnded,
  nfcScanHaptic,
  scanTokenFromUrl,
  type NfcSessionEndReason,
} from "@cntrl/native";
import { useScanCase } from "../../app/hooks/useRoadCases.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { CASE_STATE_LABEL, SCAN_TARGETS, asCaseState, type CaseState } from "./caseStates.js";
import { playScanChirp, unlockScanAudio } from "./scanSound.js";
import styles from "./roadCases.module.scss";

/** Legacy's scan-mode window: long enough for a full load-out, bounded so a forgotten session ends itself. */
export const SCAN_MODE_DURATION_MS = 2 * 60 * 60 * 1000;
/** Restart delay after a one-tag session ends — the reader needs a beat to spin down (else iOS reports 202). */
const RESTART_MS = 1500;
/** The one retry after a `session-terminated` (the hardware was still spinning down). */
const TERMINATED_RETRY_MS = 2500;

export interface ScanModeState {
  target: CaseState;
  expiresAt: number;
}

/**
 * IN-APP SCAN MODE (ART-253, the legacy `startProdCasesScanMode` behaviour). The crew picks a target state and taps
 * case after case: each tag read SETS its case to the target — never a cycle, so a double tap at the truck leaves
 * the case on the truck. iOS runs ONE tag per read session, so the session is restarted after each read; the
 * restart / stop rules per `sessionEnded` reason are legacy's, which were tuned on hardware:
 *   tag-read            → restart after 1.5s (until the 2h window closes)
 *   session-terminated  → retry once after 2.5s, then stop
 *   session-timeout     → stop with "paused" (iOS ends an idle session after ~60s)
 *   user-canceled       → stop silently
 *   error               → stop with the message
 * Everything lives in refs so a listener installed at start never acts on a stale closure.
 */
export function useCaseScanMode() {
  const scan = useScanCase();
  const [active, setActive] = useState<ScanModeState | null>(null);
  const activeRef = useRef<ScanModeState | null>(null);
  const unsubs = useRef<(() => void)[]>([]);
  const retries = useRef(0);
  const timer = useRef<number | null>(null);
  const scanRef = useRef(scan);
  scanRef.current = scan;

  const clearTimer = () => {
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const stop = useCallback(() => {
    clearTimer();
    unsubs.current.forEach((u) => u());
    unsubs.current = [];
    activeRef.current = null;
    setActive(null);
    void nfcEndReadSession();
  }, []);

  const kick = useCallback(() => {
    const a = activeRef.current;
    if (!a) return;
    if (Date.now() >= a.expiresAt) {
      stop();
      return;
    }
    const prompt = SCAN_TARGETS.find((t) => t.state === a.target)?.prompt ?? "Hold near a case sticker.";
    nfcStartReadSession(prompt).catch((e: unknown) => {
      pushToast(`Couldn't start NFC: ${e instanceof Error ? e.message : String(e)}`);
      stop();
    });
  }, [stop]);

  const onTag = useCallback((url: string) => {
    const a = activeRef.current;
    if (!a) return;
    const token = scanTokenFromUrl(url);
    if (!token) {
      pushToast("No case code on that sticker.", "warning");
      return;
    }
    scanRef.current.mutate(
      { token, state: a.target },
      {
        onSuccess: (row) => {
          nfcScanHaptic();
          playScanChirp();
          pushToast(`${row.name ?? "Case"} → ${CASE_STATE_LABEL[asCaseState(row.state)]}`, "success");
        },
        onError: (err) =>
          pushToast(
            err instanceof ApiError && err.status === 404
              ? "That sticker isn't paired to a case in this band."
              : `Couldn't update the case: ${err instanceof Error ? err.message : "try again"}`,
          ),
      },
    );
  }, []);

  const onEnded = useCallback(
    (reason: NfcSessionEndReason, message?: string) => {
      if (!activeRef.current) return;
      switch (reason) {
        case "user-canceled":
          stop();
          return;
        case "session-timeout":
          pushToast("Scan paused (no taps in 60s) — tap Scan to resume.", "info");
          stop();
          return;
        case "session-terminated":
          if (retries.current < 1) {
            retries.current += 1;
            clearTimer();
            timer.current = window.setTimeout(kick, TERMINATED_RETRY_MS);
            return;
          }
          pushToast("Scan paused — tap Scan to keep going.", "info");
          stop();
          return;
        case "error":
          pushToast(`NFC unavailable: ${message || "try again in a moment"}`);
          stop();
          return;
        default:
          retries.current = 0;
          clearTimer();
          timer.current = window.setTimeout(kick, RESTART_MS);
      }
    },
    [kick, stop],
  );

  const start = useCallback(
    async (target: CaseState) => {
      if (activeRef.current) return;
      if (!nfcAvailable()) {
        pushToast("NFC scan mode only works in the Artist CNTRL iOS app.", "info");
        return;
      }
      // Before the first await: this must run inside the picker tap's gesture, or WebKit keeps the chirp muted.
      unlockScanAudio();
      const next = { target, expiresAt: Date.now() + SCAN_MODE_DURATION_MS };
      activeRef.current = next;
      setActive(next);
      retries.current = 0;
      unsubs.current = [await onNfcTagFound(onTag), await onNfcSessionEnded(onEnded)];
      kick();
    },
    [kick, onEnded, onTag],
  );

  // Leaving the surface ends the session — a read sheet must never outlive the screen that started it.
  useEffect(() => stop, [stop]);

  return { active, start, stop };
}

/** The target picker — "every scanned case will go to this state". */
export function ScanTargetPicker({ onPick, onClose }: { onPick: (s: CaseState) => void; onClose: () => void }) {
  return (
    <Sheet title="Start scan mode" onClose={onClose}>
      <div className={styles.picker}>
        <h2 className={styles.pickerTitle}>Start scan mode</h2>
        <p className={styles.pickerSub}>Every scanned case will go to this state</p>
        <div className={styles.pickerOpts}>
          {SCAN_TARGETS.map((t) => (
            <button key={t.state} type="button" className={styles.pickerOpt} onClick={() => onPick(t.state)}>
              <span className={styles.pill} data-state={t.state}>{CASE_STATE_LABEL[t.state]}</span>
              <span className={styles.pickerOptBody}>
                <span className={styles.pickerOptTitle}>{t.title}</span>
                <span className={styles.pickerOptSub}>{t.sub}</span>
              </span>
            </button>
          ))}
        </div>
        <DialogCancel />
      </div>
    </Sheet>
  );
}

/** The running banner — target, time left, Stop. Re-renders on a slow tick only for the countdown. */
export function ScanModeBanner({ state, onStop }: { state: ScanModeState; onStop: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  const mins = Math.max(0, Math.round((state.expiresAt - now) / 60_000));
  const left = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return (
    <div className={styles.banner} role="status" data-state={state.target}>
      <span className={styles.bannerText}>
        Scanning → <strong>{CASE_STATE_LABEL[state.target]}</strong> · {left} left
      </span>
      <button type="button" className={styles.bannerStop} onClick={onStop}>Stop</button>
    </div>
  );
}
